import { buildDeviceAuthPayload } from "../../../src/gateway/device-auth.js";
import {
  GATEWAY_CLIENT_MODES,
  GATEWAY_CLIENT_NAMES,
  type GatewayClientMode,
  type GatewayClientName,
} from "../../../src/gateway/protocol/client-info.js";
import { PROTOCOL_VERSION } from "../../../src/gateway/protocol/schema/protocol-schemas.js";
import { clearDeviceAuthToken, loadDeviceAuthToken, storeDeviceAuthToken } from "./device-auth.ts";
import { loadOrCreateDeviceIdentity, signDevicePayload } from "./device-identity.ts";
import { gatewayErrorFromShape, type GatewayErrorShape } from "./gateway-errors.ts";
import { generateUUID } from "./uuid.ts";

export {
  GatewayRequestError,
  gatewayErrorFromShape,
  isGatewayRequestError,
  isRetryableGatewayError,
  retryAfterMsFromError,
  type GatewayErrorShape,
} from "./gateway-errors.ts";

export type GatewayEventFrame = {
  type: "event";
  event: string;
  payload?: unknown;
  seq?: number;
  stateVersion?: { presence: number; health: number };
};

export type GatewayResponseFrame = {
  type: "res";
  id: string;
  ok: boolean;
  payload?: unknown;
  error?: GatewayErrorShape;
};

export type GatewayHelloOk = {
  type: "hello-ok";
  protocol: number;
  features?: { methods?: string[]; events?: string[] };
  snapshot?: unknown;
  auth?: {
    deviceToken?: string;
    role?: string;
    scopes?: string[];
    issuedAtMs?: number;
  };
  policy?: { tickIntervalMs?: number };
};

type Pending = {
  resolve: (value: unknown) => void;
  reject: (err: unknown) => void;
};

export type GatewayBrowserClientOptions = {
  url: string;
  token?: string;
  password?: string;
  clientName?: GatewayClientName;
  clientVersion?: string;
  platform?: string;
  mode?: GatewayClientMode;
  instanceId?: string;
  onHello?: (hello: GatewayHelloOk) => void;
  onEvent?: (evt: GatewayEventFrame) => void;
  onClose?: (info: { code: number; reason: string }) => void;
  onGap?: (info: { expected: number; received: number; recoverable: boolean }) => void;
  /**
   * Fired when a gap is detected AND the client can recover by refetching
   * history (e.g. immediately after a reconnect). The UI should kick off a
   * `chat.history` reload. Companion to `onGap`; new code should prefer this
   * for the recoverable UX (no "refresh the page" toast).
   */
  onRecoverableGap?: (info: { expected: number; received: number }) => void;
  /**
   * Tunnel lifecycle events. Emitted on every state transition so the
   * inspector can show the chip without polling. The client does NOT
   * decide what the state should be — it only reports the raw transport
   * event and lets the host's `TunnelManager` own the state machine.
   *
   * - `connecting` — about to open a socket (or scheduling a reconnect).
   * - `healthy`   — open AND the first server `tick` round-trip succeeded.
   * - `degraded`  — open but the most recent heartbeat was stale (caller
   *                 drives the degraded threshold; the client only signals
   *                 heartbeat presence).
   * - `dead`      — socket closed with a non-normal code, or a fatal
   *                 protocol error. This is sticky: the client will not
   *                 auto-reconnect past the backoff budget; the operator
   *                 must press "Reconnect" in the inspector.
   *
   * Important: this callback is the ONLY path the gateway client uses to
   * surface transport state. The composer's `lastError` field is
   * explicitly NOT written from this path (see `app-gateway.ts`).
   */
  onTunnelEvent?: (evt: {
    state: "connecting" | "healthy" | "degraded" | "dead";
    code?: number;
    reason?: string;
  }) => void;
  /**
   * Tunnel heartbeat sample. Fired whenever the client receives a
   * server `tick` response (or any other live event). The host's
   * `TunnelManager` uses this to promote a degraded/connecting tunnel
   * back to healthy and to reset the stale-clock.
   */
  onTunnelHeartbeat?: () => void;
};

// 4008 = application-defined code (browser rejects 1008 "Policy Violation")
const CONNECT_FAILED_CLOSE_CODE = 4008;

export class GatewayBrowserClient {
  private ws: WebSocket | null = null;
  private pending = new Map<string, Pending>();
  private closed = false;
  private lastSeq: number | null = null;
  private connectNonce: string | null = null;
  private connectSent = false;
  private connectTimer: number | null = null;
  private backoffMs = 800;
  private heartbeatTimer: number | null = null;
  private lastServerActivity: number = 0;
  /** Pending reconnect timer, so `stop()` can cancel it. */
  private reconnectTimer: number | null = null;
  /** Whether the visibility/freeze/resume listeners are currently bound. */
  private visibilityHandlersBound = false;
  private onVisibilityChange: (() => void) | null = null;
  private onFreeze: (() => void) | null = null;
  private onResume: (() => void) | null = null;
  // 20-minute idle window — the operator may leave to make tea and expect
  // work to keep landing in the WebCHAT/Fleet Control transcript, not a
  // silently severed WS that "folds into activity" on return. The server
  // tick (TICK_INTERVAL_MS = 30_000) keeps the socket warm at the app
  // layer; bumping the client timeout prevents the browser from giving
  // up first. Bumped from 3min → 20min.
  //
  // Heartbeat interval is kept at 25s (not the 60s that was briefly used)
  // because background Chromium tabs throttle 60s setInterval callbacks,
  // so the socket can freeze or be dropped by proxies while the operator
  // is away. The 20min timeout still absorbs long idle periods.
  private heartbeatIntervalMs = 25000; // Send heartbeat every 25s
  private heartbeatTimeoutMs = 1200000; // Allow 20min idle before forced reconnect (was 3min)

  constructor(private opts: GatewayBrowserClientOptions) {}

  /** Returns the WS URL converted to its HTTP(S) origin. Used for fetch(). */
  getHttpOrigin(): string {
    try {
      const u = new URL(this.opts.url);
      u.protocol = u.protocol === "wss:" || u.protocol === "https:" ? "https:" : "http:";
      // `origin` is scheme+host+port only — no path, query, or hash.
      // The previous `pathname = "/"` + `toString()` kept `?token=` on
      // the gateway URL and produced `http://host/?token=x/api/chat/upload`.
      return u.origin;
    } catch {
      return "";
    }
  }

  /**
   * Returns the auth token to use for fetch() calls.
   *
   * Prefers the per-device JWT issued by the last successful WS handshake
   * (under `openclaw.device.auth.v1`). HTTP endpoints that only know how
   * to verify the static settings token will accept that; endpoints that
   * also support device tokens (such as `POST /api/chat/upload`) should
   * call {@link uploadAuth} instead so they can also forward the
   * `X-Jaisiu-Device-Id` header.
   *
   * Falls back to the static settings token/password only when no device
   * JWT exists yet (first connect, device wiped, or localStorage
   * unavailable).
   */
  /**
   * Returns the bearer token used for HTTP calls (`/api/chat/upload`,
   * `/v1/chat/completions`, etc.) AND the WS upgrade headers.
   *
   * The chat composer uses ONE credential: the static token the user
   * pasted into Settings (originating from `#token=…` in the URL). It is
   * stored in localStorage and persists for the life of the session,
   * mirroring the cookie behavior. We deliberately do NOT mint or accept
   * a separate per-device JWT for HTTP upload — users will not type a
   * second token, and the chat itself is already authenticated with this
   * one, so they are the same identity.
   */
  token(): string | undefined {
    return this.opts.token ?? this.opts.password;
  }

  start() {
    this.closed = false;
    this.lastServerActivity = Date.now();
    // Per-session/per-host tunnel refactor — surface a `connecting`
    // signal so the inspector's chip can flip from `dead` to
    // `connecting` immediately, without waiting for `open`.
    this.opts.onTunnelEvent?.({ state: "connecting" });
    this.connect();
    this.startHeartbeat();
    // Idempotent: `start()` can be called on a client that is already
    // running (e.g. a reconnect through `connectGateway`), and stacking
    // another set of listeners would retain a second closure over `this`.
    this.setupVisibilityHandler();
  }

  stop() {
    this.closed = true;
    this.stopHeartbeat();
    this.teardownVisibilityHandler();
    // Cancel a pending reconnect so it cannot open a socket nothing owns.
    if (this.reconnectTimer != null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
    this.flushPending(new Error("gateway client stopped"));
  }

  private teardownVisibilityHandler() {
    if (!this.visibilityHandlersBound) {
      return;
    }
    if (this.onVisibilityChange) {
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
      this.onVisibilityChange = null;
    }
    if (this.onFreeze) {
      window.removeEventListener("freeze", this.onFreeze);
      this.onFreeze = null;
    }
    if (this.onResume) {
      window.removeEventListener("resume", this.onResume);
      this.onResume = null;
    }
    this.visibilityHandlersBound = false;
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      if (this.closed) return;

      // Check if we've heard from server recently
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastServerActivity;

      if (timeSinceLastActivity > this.heartbeatTimeoutMs) {
        // Server appears unresponsive, close and reconnect
        console.log("[gateway] heartbeat timeout, reconnecting");
        this.ws?.close(4000, "heartbeat timeout");
        return;
      }

      // Send a ping to keep connection alive (via tick method request)
      if (this.connected) {
        void this.request("tick", {}).catch(() => {
          // Ignore errors, the heartbeat timeout will handle failures
        });
      }
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private setupVisibilityHandler() {
    // These listeners close over `this` (and through `opts.onEvent`, the
    // whole app element graph). They were previously anonymous and never
    // removed, so every `start()` added another set that `stop()` could not
    // detach. Bound as fields so teardown is possible, and guarded so a
    // repeated `start()` does not stack duplicates.
    if (this.visibilityHandlersBound) {
      return;
    }
    this.visibilityHandlersBound = true;

    // Reconnect immediately when tab becomes visible (handles browser throttling)
    this.onVisibilityChange = () => {
      if (document.visibilityState === "visible" && !this.closed) {
        const timeSinceLastActivity = Date.now() - this.lastServerActivity;
        // If we haven't heard from server in a while, reconnect
        if (timeSinceLastActivity > this.heartbeatTimeoutMs && !this.connected) {
          console.log("[gateway] tab visible and disconnected, reconnecting");
          this.connect();
        }
      }
    };
    document.addEventListener("visibilitychange", this.onVisibilityChange);

    // Handle page freeze/resume for bfcache compatibility
    this.onFreeze = () => {
      // Connection will likely be closed by browser, prepare for reconnect
      this.lastServerActivity = Date.now();
    };
    window.addEventListener("freeze", this.onFreeze);

    this.onResume = () => {
      if (!this.closed && !this.connected) {
        console.log("[gateway] page resumed, reconnecting");
        this.connect();
      }
    };
    window.addEventListener("resume", this.onResume);
  }

  get connected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private connect() {
    if (this.closed) {
      return;
    }
    // Per-session/per-host tunnel refactor — announce the connecting
    // state on every reconnect attempt so the inspector's chip can
    // reflect the in-flight state, not just the terminal state.
    this.opts.onTunnelEvent?.({ state: "connecting" });
    // Never stack sockets. `connect()` is reachable from the reconnect timer,
    // from `visibilitychange`, from `resume`, and from a direct call. Two
    // live sockets mean two `close` handlers, which means two reconnect
    // timers, which means the next generation multiplies again — a socket
    // storm that keeps event listeners and buffers alive the whole time.
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.ws = new WebSocket(this.opts.url);
    this.ws.addEventListener("open", () => {
      // Per-session/per-host tunnel refactor — the WS handshake is
      // complete; flip the chip to `healthy` from the client side. The
      // first inbound message (below) will refresh the heartbeat clock
      // and keep the state healthy even when the gateway is quiet.
      this.opts.onTunnelEvent?.({ state: "healthy" });
      this.queueConnect();
    });
    this.ws.addEventListener("message", (ev) => this.handleMessage(String(ev.data ?? "")));
    this.ws.addEventListener("close", (ev) => {
      const reason = String(ev.reason ?? "");
      this.ws = null;
      this.flushPending(new Error(`gateway closed (${ev.code}): ${reason}`));
      this.opts.onClose?.({ code: ev.code, reason });

      // Per-session/per-host tunnel refactor — surface a `dead` event
      // for every non-normal close. The host's `TunnelManager` will
      // decide whether the tunnel is sticky-dead (operator reconnect
      // required) or whether to auto-reconnect within the backoff
      // budget. The client deliberately does NOT write a tunnel text
      // into the chat composer — that was the audit-flagged bug.
      if (ev.code !== 1000) {
        this.opts.onTunnelEvent?.({ state: "dead", code: ev.code, reason });
      } else {
        // Clean close from the client side (e.g. `stop()` was called)
        // — also signal dead so the chip can leave the connecting
        // state. The host treats both the same.
        this.opts.onTunnelEvent?.({ state: "dead", code: ev.code, reason });
      }

      // Reset the sequence cursor on any non-normal close. After a transport
      // drop the broadcast counter has moved on; carrying the stale cursor
      // across reconnect guarantees a synthetic "gap" on the first event
      // after handshake. A clean 1000 close preserves lastSeq so callers can
      // still detect mid-session gaps.
      if (ev.code !== 1000) {
        this.lastSeq = null;
      }

      // Don't reconnect if explicitly closed by user
      if (this.closed) return;

      // Code 1001 = browser navigating away or tab closed - don't spam reconnects
      // Code 1000 = normal close
      // Code 1012 = service restart (handled by caller)
      if (ev.code === 1000 || ev.code === 1001) {
        // These are normal closures, use longer backoff
        this.backoffMs = Math.min(this.backoffMs * 2, 30_000);
      }

      this.scheduleReconnect();
    });
    this.ws.addEventListener("error", () => {
      // ignored; close handler will fire
    });
  }

  private scheduleReconnect() {
    if (this.closed) {
      return;
    }
    // One pending reconnect at a time. Previously the timer handle was
    // discarded, so a reconnect scheduled just before `stop()` still fired
    // and opened a socket nothing would ever close.
    if (this.reconnectTimer != null) {
      window.clearTimeout(this.reconnectTimer);
    }
    const delay = this.backoffMs;
    this.backoffMs = Math.min(this.backoffMs * 1.7, 15_000);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private flushPending(err: Error) {
    for (const [, p] of this.pending) {
      p.reject(err);
    }
    this.pending.clear();
  }

  private async sendConnect() {
    if (this.connectSent) {
      return;
    }
    this.connectSent = true;
    if (this.connectTimer !== null) {
      window.clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }

    // crypto.subtle is only available in secure contexts (HTTPS, localhost).
    // Over plain HTTP, we skip device identity and fall back to token-only auth.
    // Gateways may reject this unless gateway.controlUi.allowInsecureAuth is enabled.
    const isSecureContext = typeof crypto !== "undefined" && !!crypto.subtle;

    const scopes = ["operator.admin", "operator.approvals", "operator.pairing"];
    const role = "operator";
    let deviceIdentity: Awaited<ReturnType<typeof loadOrCreateDeviceIdentity>> | null = null;
    let canFallbackToShared = false;
    let authToken = this.opts.token;

    if (isSecureContext) {
      deviceIdentity = await loadOrCreateDeviceIdentity();
      const storedToken = loadDeviceAuthToken({
        deviceId: deviceIdentity.deviceId,
        role,
      })?.token;
      authToken = storedToken ?? this.opts.token;
      canFallbackToShared = Boolean(storedToken && this.opts.token);
    }
    const auth =
      authToken || this.opts.password
        ? {
            token: authToken,
            password: this.opts.password,
          }
        : undefined;

    let device:
      | {
          id: string;
          publicKey: string;
          signature: string;
          signedAt: number;
          nonce: string | undefined;
        }
      | undefined;

    if (isSecureContext && deviceIdentity) {
      const signedAtMs = Date.now();
      const nonce = this.connectNonce ?? undefined;
      const payload = buildDeviceAuthPayload({
        deviceId: deviceIdentity.deviceId,
        clientId: this.opts.clientName ?? GATEWAY_CLIENT_NAMES.CONTROL_UI,
        clientMode: this.opts.mode ?? GATEWAY_CLIENT_MODES.WEBCHAT,
        role,
        scopes,
        signedAtMs,
        token: authToken ?? null,
        nonce,
      });
      const signature = await signDevicePayload(deviceIdentity.privateKey, payload);
      device = {
        id: deviceIdentity.deviceId,
        publicKey: deviceIdentity.publicKey,
        signature,
        signedAt: signedAtMs,
        nonce,
      };
    }
    const params = {
      // P3-7 (2026-09-29): previous hard-code `minProtocol: 3, maxProtocol: 3`
      // silently broke every browser tab on a protocol bump. Accept
      // any protocol between 1 and the current version so older tabs
      // can connect to a server that already rolled forward, and
      // future servers can roll back without locking the user out.
      minProtocol: 1,
      maxProtocol: PROTOCOL_VERSION,
      client: {
        id: this.opts.clientName ?? GATEWAY_CLIENT_NAMES.CONTROL_UI,
        version: this.opts.clientVersion ?? "dev",
        platform: this.opts.platform ?? navigator.platform ?? "web",
        mode: this.opts.mode ?? GATEWAY_CLIENT_MODES.WEBCHAT,
        instanceId: this.opts.instanceId,
      },
      role,
      scopes,
      device,
      caps: [],
      auth,
      userAgent: navigator.userAgent,
      locale: navigator.language,
    };

    void this.request<GatewayHelloOk>("connect", params)
      .then((hello) => {
        if (hello?.auth?.deviceToken && deviceIdentity) {
          storeDeviceAuthToken({
            deviceId: deviceIdentity.deviceId,
            role: hello.auth.role ?? role,
            token: hello.auth.deviceToken,
            scopes: hello.auth.scopes ?? [],
          });
        }
        this.backoffMs = 800;
        this.opts.onHello?.(hello);
      })
      .catch(() => {
        if (canFallbackToShared && deviceIdentity) {
          clearDeviceAuthToken({ deviceId: deviceIdentity.deviceId, role });
        }
        this.ws?.close(CONNECT_FAILED_CLOSE_CODE, "connect failed");
      });
  }

  private handleMessage(raw: string) {
    // Track server activity for heartbeat
    this.lastServerActivity = Date.now();
    // Per-session/per-host tunnel refactor — every inbound frame is a
    // live signal from the gateway. Fire the heartbeat so the host's
    // `TunnelManager` can promote a `degraded` record back to `healthy`
    // and reset the stale-clock.
    this.opts.onTunnelHeartbeat?.();

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }

    const frame = parsed as { type?: unknown };
    if (frame.type === "event") {
      const evt = parsed as GatewayEventFrame;
      if (evt.event === "connect.challenge") {
        const payload = evt.payload as { nonce?: unknown } | undefined;
        const nonce = payload && typeof payload.nonce === "string" ? payload.nonce : null;
        if (nonce) {
          this.connectNonce = nonce;
          void this.sendConnect();
        }
        return;
      }
      const seq = typeof evt.seq === "number" ? evt.seq : null;
      if (seq !== null) {
        if (this.lastSeq !== null && seq > this.lastSeq + 1) {
          // A gap mid-session is NOT recoverable by resync — the radio
          // already moved on. A gap right after reconnect (lastSeq was
          // null) IS recoverable: caller should refetch history.
          const recoverable = this.lastSeq === null;
          this.opts.onGap?.({ expected: this.lastSeq + 1, received: seq, recoverable });
          if (recoverable) {
            this.opts.onRecoverableGap?.({ expected: this.lastSeq + 1, received: seq });
          }
        }
        this.lastSeq = seq;
      }
      try {
        this.opts.onEvent?.(evt);
      } catch (err) {
        console.error("[gateway] event handler error:", err);
      }
      return;
    }

    if (frame.type === "res") {
      const res = parsed as GatewayResponseFrame;
      const pending = this.pending.get(res.id);
      if (!pending) {
        return;
      }
      this.pending.delete(res.id);
      if (res.ok) {
        pending.resolve(res.payload);
      } else {
        pending.reject(gatewayErrorFromShape(res.error));
      }
      return;
    }
  }

  request<T = unknown>(method: string, params?: unknown): Promise<T> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error("gateway not connected"));
    }
    const id = generateUUID();
    const frame = { type: "req", id, method, params };
    const p = new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: (v) => resolve(v as T), reject });
    });
    this.ws.send(JSON.stringify(frame));
    return p;
  }

  private queueConnect() {
    this.connectNonce = null;
    this.connectSent = false;
    if (this.connectTimer !== null) {
      window.clearTimeout(this.connectTimer);
    }
    this.connectTimer = window.setTimeout(() => {
      void this.sendConnect();
    }, 750);
  }
}
